import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type ScenarioStatus = 'draft' | 'ready';

interface Scenario {
  id: string;
  analysisId: string;
  name: string;
  description?: string | null;
  status: ScenarioStatus;
  createdAt: string;
  updatedAt: string;
  results?: unknown;
}

const STATUS_LABEL: Record<ScenarioStatus, string> = {
  draft: 'Draft',
  ready: 'Ready',
};

const STATUS_COLOR: Record<ScenarioStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  ready: 'bg-green-50 text-green-700',
};

function fmtCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function ScenarioListRoute() {
  const { id: analysisId } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [recommendLevers, setRecommendLevers] = useState({
    includeJobGrant: true,
    includeInfrastructureGrant: true,
    includeAbatement: true,
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        if (!cancelled) setScenarios(json.scenarios as Scenario[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysisId, getToken]);

  async function runScenario(scenarioId: string) {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios/${scenarioId}/run`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Run failed: ${res.status}`);
    const json = await res.json();
    return json.scenario as Scenario;
  }

  async function handleRunAll() {
    setRunningAll(true);
    try {
      const draftScenarios = scenarios.filter((s) => s.status === 'draft');
      const updated: Scenario[] = [];
      for (const s of draftScenarios) {
        const result = await runScenario(s.id);
        updated.push(result);
      }
      setScenarios((prev) =>
        prev.map((s) => {
          const u = updated.find((u) => u.id === s.id);
          return u ?? s;
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunningAll(false);
    }
  }

  async function handlePromote(s: Scenario) {
    if (s.status !== 'ready') {
      setError('Run the scenario before promoting.');
      return;
    }
    const previousName = window.prompt(
      `Promote "${s.name}" to be the new base of this analysis?\n\nThe current base will be saved as a new scenario. Name it:`,
      'Previous version',
    );
    if (previousName === null) return; // cancelled
    setPromoting(s.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios/${s.id}/promote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ previousName: previousName.trim() || 'Previous version' }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error ?? `Promote failed: ${res.status}`);
      }
      // Promote moves the scenario into the base — kick the user back to the
      // analysis page so they see the new state with the new version row.
      navigate(`/analyses/${analysisId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPromoting(null);
    }
  }

  async function handleGenerateRecommended() {
    if (
      !recommendLevers.includeJobGrant &&
      !recommendLevers.includeInfrastructureGrant &&
      !recommendLevers.includeAbatement
    ) {
      setError('Select at least one incentive lever.');
      return;
    }
    setGenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${API}/api/v1/analyses/${analysisId}/scenarios/generate-recommended`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(recommendLevers),
        },
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error ?? `Generate failed: ${res.status}`);
      }
      const json = (await res.json()) as { scenario: Scenario };
      setShowRecommendModal(false);
      navigate(`/analyses/${analysisId}/scenarios/${json.scenario.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  }

  async function handleDelete(scenarioId: string) {
    setDeleting(scenarioId);
    try {
      const token = await getToken();
      await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setScenarios((prev) => prev.filter((s) => s.id !== scenarioId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(null);
    }
  }

  const readyCount = scenarios.filter((s) => s.status === 'ready').length;
  const draftCount = scenarios.filter((s) => s.status === 'draft').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to={`/analyses/${analysisId}`} className="text-xs text-slate-400 hover:underline">
            ← Back to analysis
          </Link>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight">Scenarios</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {readyCount} ready · {draftCount} draft · {scenarios.length} total
          </p>
        </div>
        <div className="flex gap-2">
          {draftCount > 0 && (
            <button
              type="button"
              onClick={() => void handleRunAll()}
              disabled={runningAll}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {runningAll ? 'Running…' : `Run all drafts (${draftCount})`}
            </button>
          )}
          {readyCount >= 2 && (
            <button
              type="button"
              onClick={() => navigate(`/analyses/${analysisId}/compare`)}
              className="rounded-md border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
            >
              Compare →
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowRecommendModal(true)}
            title="Auto-generate a scenario targeting an A+ grade"
            className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
          >
            ✨ Recommend A+
          </button>
          <Link
            to={`/analyses/${analysisId}/scenarios/new`}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            + New scenario
          </Link>
        </div>
      </div>

      {showRecommendModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="recommend-modal-title"
          onClick={() => !generating && setShowRecommendModal(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="recommend-modal-title" className="text-lg font-semibold text-slate-900">
              Generate Recommended Scenario
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              We&rsquo;ll build a scenario targeting an A+ grade, scaling the levers you choose
              until the rubric is met. If A+ is unreachable, we&rsquo;ll save the best achievable.
            </p>
            <fieldset className="mt-4 space-y-2">
              <legend className="sr-only">Incentive levers to include</legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={recommendLevers.includeJobGrant}
                  onChange={(e) =>
                    setRecommendLevers((r) => ({ ...r, includeJobGrant: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-slate-300"
                />
                Payroll grant
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={recommendLevers.includeInfrastructureGrant}
                  onChange={(e) =>
                    setRecommendLevers((r) => ({
                      ...r,
                      includeInfrastructureGrant: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-slate-300"
                />
                Infrastructure grant
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={recommendLevers.includeAbatement}
                  onChange={(e) =>
                    setRecommendLevers((r) => ({ ...r, includeAbatement: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-slate-300"
                />
                Property tax abatement
              </label>
            </fieldset>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRecommendModal(false)}
                disabled={generating}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleGenerateRecommended()}
                disabled={generating}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : scenarios.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm text-slate-500">No scenarios yet.</p>
          <Link
            to={`/analyses/${analysisId}/scenarios/new`}
            className="mt-3 inline-block rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            + Create first scenario
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  10-yr net benefit
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Payback
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scenarios.map((s) => {
                const res = s.results as
                  | { summary: { tenYearNetBenefits: number; paybackPeriodYears: number | null } }
                  | null
                  | undefined;
                return (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/analyses/${analysisId}/scenarios/${s.id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {s.name}
                      </Link>
                      {s.description && (
                        <p className="mt-0.5 text-xs text-slate-400 line-clamp-1">
                          {s.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[s.status]}`}
                      >
                        {STATUS_LABEL[s.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {res?.summary ? (
                        fmtCurrency(res.summary.tenYearNetBenefits)
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {res?.summary?.paybackPeriodYears != null ? (
                        `${res.summary.paybackPeriodYears.toFixed(1)} yrs`
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/analyses/${analysisId}/scenarios/${s.id}`}
                          className="text-xs text-indigo-600 hover:underline"
                        >
                          Edit
                        </Link>
                        {s.status === 'ready' && (
                          <button
                            type="button"
                            onClick={() => void handlePromote(s)}
                            disabled={promoting === s.id}
                            title="Make this scenario the new base of the analysis"
                            className="text-xs font-medium text-emerald-700 hover:underline disabled:opacity-50"
                          >
                            {promoting === s.id ? '…' : 'Promote'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleDelete(s.id)}
                          disabled={deleting === s.id}
                          className="text-xs text-red-500 hover:underline disabled:opacity-50"
                        >
                          {deleting === s.id ? '…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
